import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { useWsEvent } from '@/auth/WsProvider';

/** Модалка прогресса выгрузки зарплат учителей в 1С ЗУП. */
function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}
function fmtTime(d) { return d.toTimeString().slice(0, 8); }

export default function TeacherChargesSyncModal({ jobId, month, onClose }) {
  const [events, setEvents]   = useState([]);
  const [total,  setTotal]    = useState(null);
  const [done,   setDone]     = useState(false);
  const [summary, setSummary] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [events.length]);

  useWsEvent('sync.teacher-charges.event', (msg) => {
    if (!msg || msg.jobId !== jobId) return;
    if (msg.kind === 'start')   { setTotal(msg.total ?? null); setEvents((p) => [...p, { ...msg, time: new Date() }]); return; }
    if (msg.kind === 'summary') { setSummary({ ok: msg.okCount || 0, err: msg.errCount || 0, skipped: msg.skippedCount || 0 }); return; }
    if (msg.kind === 'done')    { setDone(true); return; }
    setEvents((p) => [...p, { ...msg, time: new Date() }]);
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const counts = events.reduce((a, e) => {
    if (e.kind === 'ok')       a.ok++;
    if (e.kind === 'error')    a.err++;
    if (e.kind === 'skip')     a.skip++;
    if (e.kind === 'progress') a.progress++;
    return a;
  }, { ok: 0, err: 0, skip: 0, progress: 0 });

  const processed = counts.ok + counts.err + counts.skip;
  const percent = total ? Math.round((processed / total) * 100) : 0;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            Выгрузка зарплат учителей в 1С ЗУП
            <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--fg-muted)', fontWeight: 400 }}>{month}</span>
          </h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          <div className="sync-progress">
            <div className="sync-progress__bar">
              <div className="sync-progress__fill" style={{ width: percent + '%' }} />
            </div>
            <div className="sync-progress__text">
              {total !== null ? (
                <>
                  Обработано {processed} из {total} ({percent}%)
                  {' · '}<span className="sync-progress__ok">✓ {counts.ok}</span>
                  {counts.err  > 0 && <> · <span className="sync-progress__err">✕ {counts.err}</span></>}
                  {counts.skip > 0 && <> · <span className="sync-progress__skip">↷ {counts.skip}</span></>}
                </>
              ) : 'Подключаемся к Node-сервису…'}
            </div>
          </div>

          <div className="sync-log" ref={listRef}>
            {events.length === 0 && <div className="ref-empty">Ожидаем события от Node-сервиса…</div>}
            {events.map((e, i) => <SyncRow key={i} event={e} />)}
          </div>

          {done && summary && (
            <div className="sync-summary">
              <Icon name="check" size={14} />
              <span>
                Готово. Успешно: <b>{summary.ok}</b>
                {summary.err     > 0 && <>, ошибок: <b style={{ color: 'var(--red-fg)' }}>{summary.err}</b></>}
                {summary.skipped > 0 && <>, пропущено: <b>{summary.skipped}</b></>}
              </span>
            </div>
          )}
          {!done && (
            <div className="muted" style={{ fontSize: 11.5 }}>
              Закрытие окна <b>не останавливает</b> синк — он продолжится в фоне.
            </div>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose}>
            {done ? 'Закрыть' : 'Скрыть окно'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SyncRow({ event: e }) {
  const time = e.time ? fmtTime(e.time) : '';
  const cls  = 'sync-row sync-row--' + e.kind;
  let icon = 'doc';
  if (e.kind === 'ok')       icon = 'check';
  if (e.kind === 'error')    icon = 'alert';
  if (e.kind === 'skip')     icon = 'eye-off';
  if (e.kind === 'progress') icon = 'clock';
  if (e.kind === 'start')    icon = 'spark';
  return (
    <div className={cls}>
      <span className="sync-row__time mono">{time}</span>
      <span className="sync-row__icon"><Icon name={icon} size={11} /></span>
      {e.kind === 'start' ? (
        <span className="sync-row__msg">Старт. Учителей: {e.total}</span>
      ) : (
        <>
          <span className="sync-row__name">{e.teacherName || '—'}</span>
          {Number(e.amount) > 0 && (
            <span className="muted mono" style={{ fontSize: 11 }}>· {fmtMoney(e.amount)} · {e.hours} ч</span>
          )}
          <span className="sync-row__msg">{e.message}</span>
        </>
      )}
    </div>
  );
}
